<!-- /frontend/src/components/common/EventTimeline.vue -->
<template>
  <div class="timeline-card">
    <div class="timeline-grid">
      <div
        class="labels-spacer"
        aria-hidden="true"
      />
      <div
        class="actions-spacer"
        aria-hidden="true"
      />
      <div
        class="timeline-header"
        aria-hidden="true"
      >
        <span
          v-for="t in tickLabels"
          :key="t"
          class="tick"
        >{{ t }}</span>
      </div>
      <!-- neue Spacer-Zelle für rechte Actions-Spalte -->
      <div
        class="actions-spacer"
        aria-hidden="true"
      />
      <div
        ref="labelsRef"
        class="labels-col"
      >
        <div
          v-for="row in lanes"
          :key="row.key"
          class="row-label"
          :class="{
            'is-student': props.mode === 'student' || props.mode === 'student-present',
            'released': (props.mode === 'student'|| props.mode === 'student-present') && rowWasReleasedFromStay(row),
            'has-notice': (props.mode === 'student'|| props.mode === 'student-present') && rowHasNotification(row),
            'present': (props.mode === 'student'|| props.mode === 'student-present') && rowIsPresentFromStay(row),
            'clickable': props.mode === 'student' || props.mode === 'student-present'
          }"
          :style="rowStyle(row)"
          :title="row.label"
          role="button"
          tabindex="0"
          @click="onRowActivate(row)"
          @keydown.space.prevent
          @keyup.enter.prevent="onRowActivate(row)"
          @keyup.space.prevent="onRowActivate(row)"
        >
          <span
            v-if="(props.mode === 'student' || props.mode === 'student-present') && rowClassLevel(row)"
            class="row-class-watermark"
            aria-hidden="true"
          >{{ rowClassLevel(row) }}</span>

          <div class="row-label-content">
            <template v-if="(props.mode === 'student' || props.mode === 'student-present')">
              <strong class="row-name">{{ row.label }}</strong>
              <span class="row-right">
                <i
                  v-if="rowWasReleasedFromStay(row)"
                  class="pi pi-sign-out release-icon"
                  aria-label="Entlassen"
                  title="Entlassen"
                />
                <i
                  v-if="rowHasNotification(row)"
                  class="pi pi-envelope notice-icon ml-2"
                  aria-label="Meldung vorhanden"
                  :title="row.notifySubject"
                />
                <span
                  v-if="rowLateHHMM(row)"
                  class="late-badge"
                  :title="'Fehlzeit (Pflichtangebote): ' + rowLateHHMM(row)"
                >
                  {{ rowLateHHMM(row) }}
                </span>
              </span>
            </template>
            <template v-else>
              <strong class="row-name">{{ row.label }}</strong>
              <span
                v-if="!!row.label"
                class="font-normal"
                title="derzeit anwesende Schüler"
              >({{ currentlyPresentCount(row) }})</span>
            </template>
          </div>
        </div>
      </div>

      <div
        ref="actionsRef"
        class="actions-col"
      >
        <div
          v-for="row in lanes"
          :key="row.key"
          class="row-action"
          :style="rowStyle(row)"
        >
          <button
            v-if="!isFutureBerlinDay(props.day) && (props.mode === 'student' || props.mode === 'student-present') && !rowHasOpenStay(row) && rowHasStudent(row)"
            type="button"
            class="checkin-btn"
            title="Ankommen"
            aria-label="Ankommen"
            @click="$emit('checkin', row)"
          >
            <i
              class="pi pi-sign-in"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      <div
        ref="tlRef"
        class="timeline-col"
      >
        <div
          v-if="showNowMarker"
          class="now-marker"
          :style="nowStyle"
          aria-hidden="true"
        />

        <div
          v-for="row in lanes"
          :key="row.key"
          class="lane"
          :style="laneStyle(row)"
        >
          <!-- Lane-weite Marker für Ankunft/Entlassung (aus student_school_stays), mehrere Stays möglich -->
          <div
            v-if="props.mode === 'student-present'"
            class="stay-markers"
            aria-hidden="true"
          >
            <template
              v-for="(st, idx) in rowStays(row)"
              :key="`${row.key}-stay-${idx}`"
            >
              <!-- Hintergrundbalken zwischen Ankunft und Entlassung -->
              <div
                v-if="st && st.arrivedAt && st.releasedAt"
                class="stay-range"
                :style="stayRangeStyle(parseLocalDateTime(st.arrivedAt), parseLocalDateTime(st.releasedAt))"
              />
              <span
                v-if="st && st.arrivedAt && isSameTimelineDay(st.arrivedAt)"
                class="stay-caret stay-caret-in"
                :style="stayMarkerStyle(parseLocalDateTime(st.arrivedAt), idx)"
                title="Ankunft in der Schule"
                aria-label="Ankunft"
              />
              <span
                v-if="st && st.releasedAt"
                class="stay-caret stay-caret-rel"
                :style="stayMarkerStyle(parseLocalDateTime(st.releasedAt), idx)"
                title="Entlassen"
                aria-label="Entlassen"
              />
            </template>
          </div>

          <div
            v-for="ev in visibleEvents(row)"
            :key="ev.id"
            class="block"
            :class="[
              {
                canceled: ev.status === 'canceled' && props.mode !== 'student-present',
                mandatory: ev.status !== 'canceled' && !!ev.isMandatory,
                optional: props.mode !== 'student-present' && ev.status !== 'canceled' && !ev.isMandatory,
                ready: props.mode !== 'student-present' && ev.status === 'ready',
                started: props.mode !== 'student-present' && ev.status === 'started',
                completed: props.mode !== 'student-present' && ev.status === 'completed',
                future: props.mode !== 'student-present' && ev.status !== 'canceled' && new Date(ev.startsAt || ev.starts_at) > new Date(),
                past: props.mode !== 'student-present' && ev.status !== 'canceled' && new Date(ev.startsAt || ev.starts_at) < new Date()
              },
              // Alle nicht stornierten Events sind klickbar – egal ob Lehrer- oder Schüler-Ansicht
              ev.status !== 'canceled' ? 'clickable' : null,
              studentClasses(ev)
            ]"
            :style="styleFor(ev)"
            :role="ev.status !== 'canceled' ? 'button' : null"
            :tabindex="ev.status !== 'canceled' ? 0 : null"
            :aria-label="ariaLabel(ev)"
            @click="ev.status !== 'canceled' && $emit('select', ev, (props.mode === 'student' || props.mode === 'student-present' ? row.label : null))"
          >
            <div class="block-label">
              <span
                class="event-name"
                :title="`${ev.subject} - ${hhmmFrom(uiDisplayStart(ev))} - ${hhmmFrom(uiDisplayEnd(ev))}${multiDayRangeText(ev) ? ' (' + multiDayRangeText(ev) + ')' : ''}`"
              >
                <template v-if="props.mode === 'student'">
                  {{ ev.subject }}
                  <span
                    v-if="teacherShortName(ev)"
                    class="teacher-inline font-normal"
                  >
                    ({{ teacherShortName(ev) }})
                  </span>
                </template>
                <template v-else>
                  {{ ev.subject }}
                </template>
              </span>

              <div
                v-if="multiDayRangeText(ev)"
                class="meta"
              >
                {{ multiDayRangeText(ev) }}
              </div>

              <div
                v-else
                class="meta"
              >
                {{ hhmmFrom(uiDisplayStart(ev)) }} - {{ hhmmFrom(uiDisplayEnd(ev)) }}
              </div>

              <div
                v-if="props.mode === 'teacher'"
                class="meta"
                title="Schüler | kommen | gehen"
              >
                {{ ev.participantsCount ?? 0 }}|{{ ev.presentCount ?? 0 }}|{{
                  ev.checkoutCount ?? 0
                }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- neue rechte Actions-Spalte für „Entlassen“ -->
      <div
        ref="actionsRightRef"
        class="actions-col actions-col-right"
      >
        <div
          v-for="row in lanes"
          :key="row.key"
          class="row-action"
          :style="rowStyle(row)"
        >
          <button
            v-if="!isFutureBerlinDay(props.day) && (props.mode === 'student' || props.mode === 'student-present') && rowHasOpenStay(row) && rowHasStudent(row)"
            type="button"
            class="checkout-btn"
            title="Entlassen"
            aria-label="Entlassen"
            @click="$emit('signout', row)"
          >
            <i
              class="pi pi-sign-out"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, onUnmounted, ref} from 'vue';

// Höhe einer „Spur“ innerhalb einer Lane (muss zu CSS .lane passen)
// Lehrer-Ansicht großzügig, Schüler-Ansicht kompakter
const TEACHER_ROW_HEIGHT = 60;
const STUDENT_ROW_HEIGHT = 60;

const props = defineProps({
  // Einzel-Zeile (Fallback)
  events: {type: Array, default: () => []},
  // Mehrzeilen-Modus: [{ key?, label, events: [] }]
  rows: {type: Array, default: () => []},
  // NEU: Der Tag (YYYY-MM-DD), für den diese Timeline gerendert wird
  day: {type: [String, Date, null], default: null},
  startTime: {type: String, default: '08:00:00'},
  endTime: {type: String, default: '16:00:00'},
  label: {type: String, default: ''},
  // Ansicht: 'teacher' | 'student'
  mode: {type: String, default: 'teacher'},
  // Karenzzeit in Minuten (Schüler-Ansicht)
  graceMinutes: {type: Number, default: 5},
  // Steuerung, ob ein Event anklickbar ist
  canSelect: {type: Function, default: () => true},
  // IDs der Schüler mit Meldung am Tag (optional)
  notifyStudentIds: {type: Array, default: () => []},
});
const emit = defineEmits(['select', 'student', 'studentInfo', 'checkin', 'signout']);

const tlRef = ref(null);
const labelsRef = ref(null);
const actionsRef = ref(null);
const actionsRightRef = ref(null);

function dmyFrom(val) {
  const d = val instanceof Date ? val : new Date(val);
  if (isNaN(d)) return '';
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(d);
}

function ymdBerlinFromDate(d) {
  if (!(d instanceof Date) || isNaN(d)) return '';
  // en-CA => YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(d);
}

function dayYmdFromProp(val) {
  if (!val) return '';
  if (typeof val === 'string') {
    const s = val.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const d = new Date(s);
    return isNaN(d) ? '' : ymdBerlinFromDate(d);
  }
  if (val instanceof Date) return isNaN(val) ? '' : ymdBerlinFromDate(val);
  return '';
}

function minutesFromHHMM(hhmm, fallback = 0) {
  const m = /^(\d{2}):(\d{2})$/.exec(String(hhmm || '').trim());
  if (!m) return fallback;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  return (Number.isFinite(h) && Number.isFinite(mi)) ? (h * 60 + mi) : fallback;
}

function dmyHiFrom(val) {
  const d = val instanceof Date ? val : new Date(val);
  if (isNaN(d)) return '';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(d);
}

// NEU: Für tagesübergreifende Angebote: "DD.MM.YYYY HH:II - DD.MM.YYYY HH:II"
function multiDayRangeText(ev) {
  const s = uiDisplayStart(ev) || plannedStart(ev);
  const e = uiDisplayEnd(ev) || plannedEnd(ev);
  if (!s || !e) return '';

  const sDay = dmyFrom(s);
  const eDay = dmyFrom(e);
  if (!sDay || !eDay) return '';

  if (sDay === eDay) return ''; // nicht tagesübergreifend → keine Zusatzzeile

  return `${dmyHiFrom(s)} - ${dmyHiFrom(e)}`;
}

/**
 * Effektiver Bereich (Minuten relativ zu props.startTime) für diesen "props.day".
 * Bei tagesübergreifend:
 * - Starttag: start = Event-Startzeit
 * - Zwischentag: start = 00:00
 * - Endtag: end = Event-Endzeit
 * - Zwischentag: end = 23:59
 * Danach Clamp ins sichtbare Fenster [startTime..endTime].
 */
function effectiveRangeForDay(rawStartDate, rawEndDate) {
  const dYmd = dayYmdFromProp(props.day);

  // Ohne day-Kontext: altes Verhalten (nur Uhrzeit)
  if (!dYmd) {
    const sMin = Math.max(0, minutesFromDayStart(rawStartDate));
    const eMin = Math.min(totalMinutes.value, minutesFromDayStart(rawEndDate));
    const a = Math.min(sMin, eMin);
    const b = Math.max(sMin, eMin);
    return {ok: true, startMin: a, endMin: b};
  }

  const sYmd = ymdBerlinFromDate(rawStartDate);
  const eYmd = ymdBerlinFromDate(rawEndDate);
  if (!sYmd || !eYmd) return {ok: false, startMin: 0, endMin: 0};
  if (dYmd < sYmd || dYmd > eYmd) return {ok: false, startMin: 0, endMin: 0};

  const windowStartMin = 0;
  const windowEndMin = totalMinutes.value;

  // startTime als Minuten seit 00:00
  const startTimeMin = minutesFromHHMM(String(props.startTime || '').slice(0, 5), 0);

  // Event-Uhrzeiten als Minuten seit 00:00
  const startClockMin = rawStartDate.getHours() * 60 + rawStartDate.getMinutes();
  const endClockMin = rawEndDate.getHours() * 60 + rawEndDate.getMinutes();

  // Umrechnung Uhrzeit -> Minuten relativ zu startTime
  const startRel = startClockMin - startTimeMin;
  const endRel = endClockMin - startTimeMin;

  // Zwischentage: 00:00 bzw. 23:59 annehmen
  const relAtMidnight = 0 - startTimeMin;
  const relAtDayEnd = (23 * 60 + 59) - startTimeMin;

  const rawStartRel = (dYmd === sYmd) ? startRel : relAtMidnight;
  const rawEndRel = (dYmd === eYmd) ? endRel : relAtDayEnd;

  const clampedStart = Math.max(windowStartMin, Math.min(windowEndMin, rawStartRel));
  const clampedEnd = Math.max(windowStartMin, Math.min(windowEndMin, rawEndRel));

  const a = Math.min(clampedStart, clampedEnd);
  const b = Math.max(clampedStart, clampedEnd);

  return {ok: true, startMin: a, endMin: b};
}

const lanes = computed(() => {
  function prepRows(rows) {
    return rows.map((r, i) => {
      const key = r.key ?? String(i);
      const label = String(r.label ?? `Zeile ${i + 1}`);
      const events = (Array.isArray(r.events) ? r.events : []).map(e => ({...e}));
      // zeitlich sortieren (für schülerbezogene Zustände)
      events.sort((a, b) => {
        const ad = new Date(a.startsAt || a.starts_at);
        const bd = new Date(b.startsAt || b.starts_at);
        return ad - bd;
      });

      // Nahtlos folgende Events: Ende == Start des nächsten Events
      for (let idx = 0; idx < events.length - 1; idx++) {
        const curr = events[idx];
        const next = events[idx + 1];
        if (!curr || !next) continue;

        const currEnd = minutesFromDayStart(curr.endsAt || curr.ends_at);
        const nextStart = minutesFromDayStart(next.startsAt || next.starts_at);

        if (!Number.isNaN(currEnd) && !Number.isNaN(nextStart) && currEnd === nextStart) {
          // Merker: dieser Block soll minimal kürzer gerendert werden
          curr.__hasSeamlessNext = true;
        }
      }

      // Canceled-Intervalle (in Minuten) sammeln
      const canceledRanges = events
          .filter(e => e && e.status === 'canceled')
          .map(e => {
            const s = minutesFromDayStart(e.startsAt || e.starts_at);
            const en = minutesFromDayStart(e.endsAt || e.ends_at);
            return [Math.min(s, en), Math.max(s, en)];
          });

      // Pro Event Overlap mit canceled markieren
      for (const ev of events) {
        if (!ev) continue;
        if (ev.status === 'canceled') {
          ev.__isCanceled = true;
          ev.__overCanceled = false;
          continue;
        }
        const s = minutesFromDayStart(ev.startsAt || ev.starts_at);
        const en = minutesFromDayStart(ev.endsAt || ev.ends_at);
        const a = Math.min(s, en);
        const b = Math.max(s, en);
        ev.__overCanceled = canceledRanges.some(([cs, ce]) => a < ce && b > cs);
      }

      // Schüler-Ansicht: "entlassen" pausiert Folge-Angebote bis zu neuem Checkin
      if (props.mode === 'student' || props.mode === 'student-present') {
        let paused = false;

        for (const ev of events) {
          ev.__pausedAfterRelease = paused;
          const hasCheckin = !!readCheckin(ev);
          const released = isReleased(ev);
          if (hasCheckin) paused = false;
          if (released) paused = true;
        }
      }

      // Präsenz-Nahtlosigkeit (Checkin/Checkout): Ende == Start des nächsten besuchten Events
      if (props.mode === 'student-present') {
        const present = events.filter(e => !!readCheckin(e));
        present.sort((a, b) => {
          const aStart = minutesFromDayStart(readCheckin(a));
          const bStart = minutesFromDayStart(readCheckin(b));
          return aStart - bStart;
        });
        for (let idx = 0; idx < present.length - 1; idx++) {
          const curr = present[idx];
          const next = present[idx + 1];
          const currCheckout = readCheckout(curr);
          if (!currCheckout) continue;
          const endMin = minutesFromDayStart(currCheckout);
          const nextStartMin = minutesFromDayStart(readCheckin(next));
          if (!Number.isNaN(endMin) && !Number.isNaN(nextStartMin) && endMin === nextStartMin) {
            curr.__hasSeamlessNextPresence = true;
          }
        }
      }

      // Lehrer-Ansicht: überlappende (nicht abgesagte) Events auf Spuren verteilen
      const maxTrack = assignTracks(events);

      // Anzahl Spuren je Zeile an die Events hängen (für kompakte Schüler-Ansicht)
      for (const ev of events) {
        if (ev && typeof ev === 'object') {
          ev.__tracksInRow = maxTrack;
        }
      }

      const base = {...r};
      base.key = key;
      base.label = label;
      base.events = events;
      base.__maxTrack = maxTrack;
      return base;
    });
  }

  if (Array.isArray(props.rows) && props.rows.length > 0) {
    return prepRows(props.rows);
  }
  // Fallback: Single-Lane aus events
  return prepRows([{
    key: 'single',
    label: props.label || '',
    events: Array.isArray(props.events) ? props.events : []
  }]);
});

function rowClassLevel(row) {
  const keys = Array.isArray(row?.classKeys) ? row.classKeys : [];
  for (const k of keys) {
    const m = String(k || '').match(/(\d{1,2})/);
    if (m) return m[1];
  }
  return '';
}

function currentlyPresentCount(row) {
  const events = visibleEvents(row) || [];
  let count = 0;

  events.forEach(e => {
    // Backend liefert bereits die korrekte aktuelle Präsenz-Zählung
    const currentlyPresent = Number(e.currentlyPresentCount ?? 0);
    count += currentlyPresent;
  });

  return count;
}

function hhmmFrom(val) {
  const s = String(val || '');
  const m = s.match(/(\d{2}):(\d{2})/);
  if (m) return `${m[1]}:${m[2]}`;
  const d = new Date(val);
  if (!isNaN(d)) {
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }
  return '';
}

function teacherShortName(ev) {
  const fn = (ev?.teacherFirstName || ev?.teacher_first_name || '').trim();
  let ln = (ev?.teacherLastName || ev?.teacher_last_name || '').trim();

  // Fallback: voller Name aus teacherName zerlegen
  if (!ln) {
    const full = (ev?.teacherName || ev?.teacher_name || '').trim();
    if (full) {
      const parts = full.split(/\s+/);
      ln = parts[parts.length - 1] || '';
    }
  }

  if (!fn && !ln) return '';
  const initial = fn ? `${fn.charAt(0).toUpperCase()}.` : '';
  return `${initial} ${ln}`.trim();
}

function toTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
}

const totalMinutes = computed(() => {
  const [sh, sm,] = props.startTime.split(':').map(Number);
  const [eh, em,] = props.endTime.split(':').map(Number);
  return (eh * 60 + em) - (sh * 60 + sm);
});

const tickLabels = computed(() => {
  const [sh, sm] = props.startTime.split(':').map(Number);
  const [eh, em] = props.endTime.split(':').map(Number);
  const total = (eh * 60 + em) - (sh * 60 + sm);
  const step = 120; // alle 2 Stunden
  const labels = [];

  function toHHMM(h, m) {
    const hh = String(h).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  for (let off = 0; off <= total; off += step) {
    const mins = sh * 60 + sm + off;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    labels.push(toHHMM(h, m));
  }
  const endLabel = toHHMM(eh, em);
  if (labels[labels.length - 1] !== endLabel) labels.push(endLabel);
  return labels;
});

function minutesFromDayStart(date) {
  const d = new Date(date);
  const [sh, sm,] = props.startTime.split(':').map(Number);
  return d.getHours() * 60 + d.getMinutes() - (sh * 60 + sm);
}

// Hilfsfunktion: Tracks pro Zeile berechnen, damit sich überlappende Angebote nicht überlagern
function assignTracks(events) {
  // nur nicht-abgesagte Events für Track-Berechnung
  const items = events
      .filter(e => e && e.status !== 'canceled')
      .map(e => {
        const start = minutesFromDayStart(e.startsAt || e.starts_at);
        const end = minutesFromDayStart(e.endsAt || e.ends_at);
        return {
          ev: e,
          start: Math.min(start, end),
          end: Math.max(start, end),
        };
      })
      .sort((a, b) => a.start - b.start || a.end - b.end);

  const active = []; // { end, track }
  let maxTrackIndex = -1;

  for (const item of items) {
    // abgelaufene Events aus aktiver Liste entfernen
    for (let i = active.length - 1; i >= 0; i--) {
      if (active[i].end <= item.start) {
        active.splice(i, 1);
      }
    }

    // kleinste freie Spur (0,1,2,...) suchen
    const usedTracks = new Set(active.map(a => a.track));
    let track = 0;
    while (usedTracks.has(track)) {
      track += 1;
    }

    item.ev.__track = track;
    active.push({end: item.end, track});
    if (track > maxTrackIndex) {
      maxTrackIndex = track;
    }
  }

  // Anzahl Spuren (mindestens 1, auch wenn es nur canceled-Events gibt)
  return maxTrackIndex >= 0 ? maxTrackIndex + 1 : 1;
}

// Hilfsfunktionen für Schüler-Ansicht
function readDate(ev, keys) {
  for (const k of keys) {
    const v = ev?.[k];
    if (!v) continue;
    const d = new Date(v);
    if (!isNaN(d)) return d;
  }
  return null;
}

function readBool(ev, keys) {
  for (const k of keys) {
    const v = ev?.[k];
    if (typeof v === 'boolean') return v;
    if (v === 1 || v === '1') return true;
  }
  return false;
}

function readCheckin(ev) {
  return readDate(ev, ['checkinAt', 'checkin_at', 'checkin', 'checkedInAt', 'checked_in_at']);
}

function readCheckout(ev) {
  return readDate(ev, ['checkoutAt', 'checkout_at', 'checkout', 'checkedOutAt', 'checked_out_at']);
}

function isFutureBerlinDay(dayVal) {
  const dYmd = dayYmdFromProp(dayVal);
  if (!dYmd) return false;
  const todayYmd = ymdBerlinFromDate(new Date());
  if (!todayYmd) return false;
  return dYmd > todayYmd;
}

function isReleased(ev) {
  const dt = readDate(ev, ['releasedAt', 'released_at']);
  if (dt) return true;
  return readBool(ev, ['released', 'isReleased']);
}

// Bevorzugte Anzeigebasis für Zeiten:
// - zuerst Attendance (checkin/checkout)
// - falls nicht vorhanden, geplante offer_events-Zeiten.
function eventDisplayStart(ev) {
  const ci = readCheckin(ev);
  if (ci) return ci;
  return readDate(ev, ['startsAt', 'starts_at']);
}

function eventDisplayEnd(ev) {
  const co = readCheckout(ev);
  if (co) return co;
  return readDate(ev, ['endsAt', 'ends_at']);
}

// Geplante Zeiten (offer_events)
function plannedStart(ev) {
  return readDate(ev, ['startsAt', 'starts_at']);
}

function plannedEnd(ev) {
  return readDate(ev, ['endsAt', 'ends_at']);
}

// UI-Anzeigezeiten je Modus:
// - student-present: Attendance (checkin/checkout), ggf. Fallback intern
// - teacher/student: geplante offer_events-Zeiten
function uiDisplayStart(ev) {
  return props.mode === 'student-present' ? eventDisplayStart(ev) : plannedStart(ev);
}

function uiDisplayEnd(ev) {
  return props.mode === 'student-present' ? eventDisplayEnd(ev) : plannedEnd(ev);
}

/**
 * Zusätzliche Attendance-Blöcke robust einsammeln.
 * Unterstützt mögliche Feldvarianten:
 * - ev.attendanceExtras, ev.attendance_extras, ev.extras, ev.extraAttendances, ev.blocks
 * sowie Block-Felder: blockIndex|block_index, checkinAt|checkin_at, checkoutAt|checkout_at.
 */
function normalizeExtraBlocks(ev) {
  const out = [];
  const candidates = [];
  if (Array.isArray(ev?.attendanceExtras)) candidates.push(...ev.attendanceExtras);
  if (Array.isArray(ev?.attendance_extras)) candidates.push(...ev.attendance_extras);
  if (Array.isArray(ev?.extras)) candidates.push(...ev.extras);
  if (Array.isArray(ev?.extraAttendances)) candidates.push(...ev.extraAttendances);
  if (Array.isArray(ev?.blocks)) candidates.push(...ev.blocks);

  for (const b of candidates) {
    const bi = Number(b?.blockIndex ?? b?.block_index ?? b?.index ?? b?.idx ?? b?.block ?? 0);
    const ci = readDate(b, ['checkinAt', 'checkin_at', 'checkedInAt', 'checked_in_at']);
    const co = readDate(b, ['checkoutAt', 'checkout_at', 'checkedOutAt', 'checked_out_at']);
    if (ci || co) {
      out.push({
        blockIndex: bi > 0 ? bi : 1,
        checkin_at: ci || null,
        checkout_at: co || null,
        _src: b
      });
    }
  }
  return out;
}

/**
 * Erzeugt „virtuelle Events“ pro Attendance-Block:
 * - Root-Block (b0), falls Check-in/Checkout vorhanden
 * - Je extra-Block (b1..n) ein Duplikat mit den Block-Zeiten
 * IDs werden stabil aus Basis-ID + Suffix aufgebaut.
 */
function explodeAttendanceBlocks(ev) {
  const list = [];
  const rootCi = readCheckin(ev);
  const rootCo = readCheckout(ev);

  const baseId =
      ev?.id ??
      ev?.eventId ??
      ev?.event_id ??
      ev?._id ??
      `${ev?.subject || ev?.topic || 'event'}`;

  if (rootCi || rootCo) {
    list.push({
      ...ev,
      id: `${baseId}-b0`,
      checkinAt: rootCi || ev?.checkinAt || ev?.checkin_at || null,
      checkin_at: rootCi || ev?.checkin_at || ev?.checkinAt || null,
      checkoutAt: rootCo || ev?.checkoutAt || ev?.checkout_at || null,
      checkout_at: rootCo || ev?.checkout_at || ev?.checkoutAt || null
    });
  }

  const extras = normalizeExtraBlocks(ev);
  let n = 1;
  for (const x of extras) {
    list.push({
      ...ev,
      id: `${baseId}-b${x.blockIndex || n}`,
      checkinAt: x.checkin_at || null,
      checkin_at: x.checkin_at || null,
      checkoutAt: x.checkout_at || null,
      checkout_at: x.checkout_at || null
    });
    n++;
  }
  return list;
}


/**
 * Gibt zurück, ob ein offener Stay (aus student_school_stays) existiert.
 * Erwartetes Feld am Row-Objekt: hasOpenStay:boolean
 * Default: false, falls keine Information vorhanden ist.
 */
function rowHasOpenStay(row) {
  const v = row?.hasOpenStay;
  if (typeof v === 'boolean') return v;
  if (v === 1 || v === '1') return true;
  if (v === 0 || v === '0') return false;
  return false;
}

/**
 * Prüft, ob die Row eine Schüler-ID trägt.
 */
function rowHasStudent(row) {
  const n = row?.studentId;

  return (Number.isFinite(n) && n > 0);
}

/**
 * Schüler ist aktuell in der Schule, wenn hasOpenStay === true.
 */
function rowIsPresentFromStay(row) {
  return rowHasOpenStay(row) === true;
}

/**
 * Schüler war heute schon da und ist inzwischen gegangen,
 * wenn hasOpenStay === false und lastReleaseAt gesetzt ist.
 */
function rowWasReleasedFromStay(row) {
  if (rowHasOpenStay(row) === true) return false;
  const lastRel = row?.lastReleaseAt ?? null;
  return !!lastRel;
}

// Stay-Marker Helfer (Zeiten aus student_school_stays)
function parseLocalDateTime(s) {
  if (!s) return null;
  // 'YYYY-MM-DD HH:mm' robust in lokalen Kontext überführen
  const str = String(s).trim().replace(' ', 'T');
  const d = new Date(str);
  return isNaN(d) ? null : d;
}

// Liefert normalisierte Stays-Liste [{arrivedAt, releasedAt, id?}, ...]
function rowStays(row) {
  const list = Array.isArray(row?.stays) ? row.stays : [];
  return list
      .map(st => {
        const a = st?.arrivedAt || st?.arrived_at || null;
        const r = st?.releasedAt || st?.released_at || null;
        const id = st?.id ?? null;
        return {arrivedAt: a, releasedAt: r, id};
      })
      .filter(st => st.arrivedAt || st.releasedAt);
}

// NEU: Caret "Ankunft" nur am selben Timeline-Tag anzeigen (YYYY-MM-DD Vergleich)
function isSameTimelineDay(localYmdHi) {
  const dYmd = dayYmdFromProp(props.day);
  if (!dYmd) return false;

  const s = String(localYmdHi || '').trim();
  if (s.length < 10) return false;

  // arrivedAt/releasedAt kommen als 'YYYY-MM-DD HH:mm' (Berlin-Anzeige)
  return s.slice(0, 10) === dYmd;
}

// Hintergrundbalken zwischen Ankunft und Entlassung
function stayRangeStyle(dtStart, dtEnd) {
  if (!dtStart || !dtEnd) return {display: 'none'};
  const sMin = Math.max(0, Math.min(totalMinutes.value, minutesFromDayStart(dtStart)));
  const eMin = Math.max(0, Math.min(totalMinutes.value, minutesFromDayStart(dtEnd)));
  const a = Math.min(sMin, eMin);
  const b = Math.max(sMin, eMin);
  const leftPct = (a / totalMinutes.value) * 100;
  const widthPct = Math.max(0, ((b - a) / totalMinutes.value) * 100);

  return {
    left: `${leftPct}%`,
    width: `calc(${widthPct}% + 7px)`,
  };
}

function stayMarkerStyle(dt, idx = 0) {
  if (!dt) return {display: 'none'};
  // X-Position prozentual relativ zum Tagesfenster
  const mins = Math.max(0, Math.min(totalMinutes.value, minutesFromDayStart(dt)));
  const pct = (mins / totalMinutes.value) * 100;
  // Y-Position alternierend je Stay-Index: 30% / 70%
  const top = (Number(idx) % 2 === 0) ? '40%' : '60%';

  return {left: `${pct}%`, top};
}

function rowHasNotification(row) {
  // direkte Flags am Row-Objekt
  if (row && row.hasNotification) return true;

  // übergebene ID-Liste (robust: id, studentId, student_id, key vergleichen)
  const ids = Array.isArray(props.notifyStudentIds) ? props.notifyStudentIds : [];
  if (ids.length > 0 && row) {
    const wanted = new Set(ids.map(String));
    const candidates = [row.id, row.studentId, row.student_id, row.key].filter(v => v !== undefined && v !== null);
    if (candidates.some(c => wanted.has(String(c)))) return true;
  }

  // optional: Event-Felder prüfen
  if (Array.isArray(row?.events)) {
    for (const e of row.events) {
      if (e?.notifyAccepted || e?.hasNotification) return true;
    }
  }
  return false;
}

function rowLateSeconds(row) {
  const events = Array.isArray(row?.events) ? row.events : [];
  let sum = 0;
  for (const e of events) {
    // nur Pflichtangebote zählen, canceled ignorieren
    const mandatory = !!(e?.isMandatory ?? e?.mandatory);
    if (!mandatory || e?.status === 'canceled') continue;

    // Reale Werte ausschließlich aus attendance.lateness_seconds
    const provided = Number(e?.lateness_seconds ?? e?.latenessSeconds);
    if (Number.isFinite(provided) && provided > 0) {
      sum += provided;
    }
  }
  return sum;
}

function secondsToHHMM(sec) {
  const n = Number(sec);
  if (!Number.isFinite(n) || n <= 0) return '';
  const mins = Math.floor(n / 60);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function rowLateHHMM(row) {
  const secs = rowLateSeconds(row);
  return secs > 0 ? secondsToHHMM(secs) : '';
}

function studentClasses(ev) {
  if (props.mode !== 'student-present' || ev?.status === 'canceled') return {};
  const checkin = readCheckin(ev);
  if (!checkin) return {};

  const checkout = readCheckout(ev);
  const released = isReleased(ev);

  const classes = {};
  // Caret-Icons außerhalb des Blocks sichtbar machen
  classes['st-ext-icons'] = true;

  // Nur echte Pflichtangebote als mandatory markieren
  const isMandatory = !!(ev?.isMandatory ?? ev?.mandatory);
  if (isMandatory) {
    classes['mandatory'] = true;
  }

  // Aktuell besucht: kein Checkout und nicht entlassen → intensiveres Grün
  if (!checkout && !released) {
    classes['st-visited-current'] = true;
  } else {
    classes['st-visited'] = true;
  }

  return classes;
}

function visibleEvents(row) {
  const events = Array.isArray(row?.events) ? row.events : [];
  if (props.mode !== 'student-present') return events;

  // NEU: In "student-present" niemals Zukunft rendern (hartes UI-Verbot)
  if (isFutureBerlinDay(props.day)) return [];

  // Präsenzmodus: pro Event alle Attendance-Blöcke (Root + Extras) „explodieren“
  const fragments = [];
  for (const ev of events) {
    const list = explodeAttendanceBlocks(ev);
    if (Array.isArray(list) && list.length) fragments.push(...list);
  }

  // Nur Fragmente mit Check-in darstellen (Start Pflicht).
  // Ende ist optional: ohne Checkout wird der Block bis zur Jetzt-Markierung gezeichnet.
  const present = fragments.filter(e => !!readCheckin(e));
  present.sort((a, b) => {
    const ad = readCheckin(a);
    const bd = readCheckin(b);
    return (ad?.getTime?.() || 0) - (bd?.getTime?.() || 0);
  });
  return present;
}

// Lane-Höhe dynamisch nach Modus
// - teacher: wächst mit Tracks (60px je Spur)
// - student: bei Overlap 36px pro Track, zzgl. 2px Abstand zwischen Tracks; ohne Overlap 60px
// - student-present: fix 60px (Template-Rahmen)
function laneStyle(row) {
  const tracks = row?.__maxTrack || 1;

  if (props.mode === 'teacher') {
    return {
      height: `${TEACHER_ROW_HEIGHT * tracks}px`
    };
  }

  if (props.mode === 'student') {
    const trackGap = 2;
    if (tracks > 1) {
      // 36px je Track + (tracks-1)*2px vertikaler Abstand + 6px Padding
      return {height: `${36 * tracks + trackGap * (tracks - 1) + 6}px`};
    }
    // keine Überlappung → volle Zeilenhöhe
    return {height: `${STUDENT_ROW_HEIGHT}px`};
  }

  // student-present: fix 60px
  return {height: `${TEACHER_ROW_HEIGHT}px`};
}

// Labels-Spalte an gleiche Spuranzahl koppeln
function rowStyle(row) {
  const tracks = row?.__maxTrack || 1;

  if (props.mode === 'teacher') {
    return {
      height: `${TEACHER_ROW_HEIGHT * tracks - 6}px`
    };
  }

  if (props.mode === 'student') {
    const trackGap = 2;
    if (tracks > 1) {
      // 36px je Track + (tracks-1)*2px vertikaler Abstand (Lane-Padding bereits abgezogen)
      return {height: `${36 * tracks + trackGap * (tracks - 1)}px`};
    }
    return {height: `${STUDENT_ROW_HEIGHT - 6}px`};
  }

  // student-present: fix 60px → -6px für Padding/Margins
  return {height: `${TEACHER_ROW_HEIGHT - 6}px`};
}

function styleFor(ev) {
  const canceledBand = 5;      // Höhe für canceled
  const canceledTopOffset = 2; // Abstand unterhalb des Canceled-Bands

  // Lehrer-Ansicht
  if (props.mode === 'teacher') {
    const startDate = new Date(ev.startsAt || ev.starts_at);
    const endDate = new Date(ev.endsAt || ev.ends_at);
    if (isNaN(startDate) || isNaN(endDate)) return {display: 'none'};

    const eff = effectiveRangeForDay(startDate, endDate);
    if (!eff.ok) return {display: 'none'};

    const leftPct = (eff.startMin / totalMinutes.value) * 100;
    const baseWidthPct = Math.max(2, ((eff.endMin - eff.startMin) / totalMinutes.value) * 100);

    const trackIndex = typeof ev.__track === 'number' ? ev.__track : 0;
    const baseTopForTrack = 3 + trackIndex * TEACHER_ROW_HEIGHT;
    const maxBlockHeight = TEACHER_ROW_HEIGHT - 6;

    if (ev && ev.status === 'canceled') {
      return {
        left: `${leftPct}%`,
        width: `${baseWidthPct}%`,
        top: `3px`,
        height: `${canceledBand}px`
      };
    }

    const overCanceled = !!ev?.__overCanceled;

    const topPx = overCanceled
        ? baseTopForTrack + canceledBand + canceledTopOffset
        : baseTopForTrack;

    const heightPx = overCanceled
        ? Math.max(0, maxBlockHeight - canceledBand - canceledTopOffset)
        : maxBlockHeight;

    const widthValue = ev.__hasSeamlessNext
        ? `calc(${baseWidthPct}% - 2px)`
        : `${baseWidthPct}%`;

    return {
      left: `${leftPct}%`,
      width: widthValue,
      top: `${topPx}px`,
      height: `${heightPx}px`
    };
  }

  // Schüler-Präsenz-Ansicht: tatsächliche Präsenz (Check-in → Check-out bzw. Jetzt)
  if (props.mode === 'student-present') {
    // NEU: zusätzliche Absicherung – in Zukunft niemals rendern
    if (isFutureBerlinDay(props.day)) {
      return {display: 'none'};
    }

    const checkin = readCheckin(ev);
    const checkout = readCheckout(ev);

    // Ohne jeglichen Zeitstempel keine Darstellung
    if (!checkin && !checkout) {
      return {display: 'none'};
    }

    // Sonderfall: nur Checkout vorhanden → als schmale Markierung darstellen
    if (!checkin && checkout) {
      const posMin = Math.max(0, Math.min(totalMinutes.value, minutesFromDayStart(checkout)));
      const leftPct = (posMin / totalMinutes.value) * 100;
      const topPx = 3;
      const heightPx = TEACHER_ROW_HEIGHT - 6;
      return {
        left: `${leftPct}%`,
        width: `2px`,
        top: `${topPx}px`,
        height: `${heightPx}px`
      };
    }

    // NEU: Wenn checkout fehlt, dann Ende abhängig vom dargestellten Tag:
    // - future day: nicht rendern
    // - today: bis jetzt
    // - past day: bis 23:59
    let effectiveEndDate = checkout;

    if (!effectiveEndDate) {
      const dYmd = dayYmdFromProp(props.day);
      if (dYmd) {
        const todayYmd = ymdBerlinFromDate(new Date());

        // Zukunft -> darf NICHT bis now gerendert werden
        if (todayYmd && dYmd > todayYmd) {
          return {display: 'none'};
        }

        if (todayYmd && dYmd === todayYmd) {
          effectiveEndDate = new Date(); // heute -> bis jetzt
        } else {
          // Vergangenheitstag -> bis 23:59 lokaler Tag
          const endOfDayLocal = new Date(`${dYmd}T23:59:00`);
          effectiveEndDate = isNaN(endOfDayLocal) ? new Date() : endOfDayLocal;
        }
      } else {
        // Fallback (sollte bei euch dank :day="date" kaum greifen)
        effectiveEndDate = new Date();
      }
    }

    // Range tagesübergreifend korrekt clampen (00:00/23:59 auf Zwischen-/Endtagen)
    const eff = effectiveRangeForDay(checkin, effectiveEndDate);
    if (!eff.ok) return {display: 'none'};

    const leftPct = (eff.startMin / totalMinutes.value) * 100;
    const baseWidthPct = Math.max(2, ((eff.endMin - eff.startMin) / totalMinutes.value) * 100);

    const topPx = 3;
    const heightPx = TEACHER_ROW_HEIGHT - 6;

    const widthValue = (ev.__hasSeamlessNextPresence || ev.__hasSeamlessNext)
        ? `calc(${baseWidthPct}% - 2px)`
        : `${baseWidthPct}%`;

    return {
      left: `${leftPct}%`,
      width: widthValue,
      top: `${topPx}px`,
      height: `${heightPx}px`
    };
  }

  // Schüler-Ansicht (Planzeiten)
  const startDate = new Date(ev.startsAt || ev.starts_at);
  const endDate = new Date(ev.endsAt || ev.ends_at);
  if (isNaN(startDate) || isNaN(endDate)) return {display: 'none'};

  const eff = effectiveRangeForDay(startDate, endDate);
  if (!eff.ok) return {display: 'none'};

  const leftPct = (eff.startMin / totalMinutes.value) * 100;
  const baseWidthPct = Math.max(2, ((eff.endMin - eff.startMin) / totalMinutes.value) * 100);

  const trackIndex = typeof ev.__track === 'number' ? ev.__track : 0;

  if (ev && ev.status === 'canceled') {
    return {
      left: `${leftPct}%`,
      width: `${baseWidthPct}%`,
      top: `3px`,
      height: `${canceledBand}px`
    };
  }

  const overCanceled = !!ev?.__overCanceled;
  const tracksInRow = Number(ev?.__tracksInRow ?? 1);
  const baseBlockHeight = STUDENT_ROW_HEIGHT - 6;
  const blockHeight = tracksInRow > 1 ? 36 : baseBlockHeight;

  const trackGap = tracksInRow > 1 ? 2 : 0;
  const baseTopForTrack = 3 + trackIndex * (blockHeight + trackGap);

  const topPx = overCanceled
      ? baseTopForTrack + canceledBand + canceledTopOffset
      : baseTopForTrack;

  const maxBlockHeight = blockHeight;
  const heightPx = overCanceled
      ? Math.max(0, maxBlockHeight - canceledBand - canceledTopOffset)
      : maxBlockHeight;

  const widthValue = ev.__hasSeamlessNext
      ? `calc(${baseWidthPct}% - 2px)`
      : `${baseWidthPct}%`;

  return {
    left: `${leftPct}%`,
    width: widthValue,
    top: `${topPx}px`,
    height: `${heightPx}px`
  };
}

const nowStyle = ref({left: '-100%'});
const nowMinutes = ref(0);
const showNowMarker = ref(false);

function updateNow() {
  const now = new Date();
  const mins = minutesFromDayStart(now);
  const pct = (mins / totalMinutes.value) * 100;
  nowStyle.value = {left: `${pct}%`};
  nowMinutes.value = Math.max(0, Math.min(totalMinutes.value, mins));

  // Sichtbar nur zwischen 07:00 und 17:00 Uhr (lokale Zeit)
  const minutesOfDay = now.getHours() * 60 + now.getMinutes();
  showNowMarker.value = minutesOfDay >= 7 * 60 && minutesOfDay <= 17 * 60;
}

let timer;
let scrollHandlerAttached = false;
let scrollSyncLock = false;

function onColScroll(e) {
  if (scrollSyncLock) return;
  const src = e.target;
  const labelsEl = labelsRef.value;
  const tlEl = tlRef.value;
  const actionsEl = actionsRef.value;
  scrollSyncLock = true;
  if (src === labelsEl) {
    if (tlEl) tlEl.scrollTop = src.scrollTop;
    if (actionsEl) actionsEl.scrollTop = src.scrollTop;
  } else if (src === tlEl) {
    if (labelsEl) labelsEl.scrollTop = src.scrollTop;
    if (actionsEl) actionsEl.scrollTop = src.scrollTop;
  } else if (src === actionsEl) {
    if (labelsEl) labelsEl.scrollTop = src.scrollTop;
    if (tlEl) tlEl.scrollTop = src.scrollTop;
  }
  scrollSyncLock = false;
}

onMounted(() => {
  updateNow();
  timer = setInterval(updateNow, 60 * 1000);

  const labelsEl = labelsRef.value;
  const tlEl = tlRef.value;
  const actionsEl = actionsRef.value;
  if (labelsEl && tlEl && actionsEl && !scrollHandlerAttached) {
    labelsEl.addEventListener('scroll', onColScroll, {passive: true});
    tlEl.addEventListener('scroll', onColScroll, {passive: true});
    actionsEl.addEventListener('scroll', onColScroll, {passive: true});
    scrollHandlerAttached = true;
  }
});
onUnmounted(() => {
  if (timer) clearInterval(timer);
  const labelsEl = labelsRef.value;
  const tlEl = tlRef.value;
  const actionsEl = actionsRef.value;
  if (scrollHandlerAttached) {
    if (labelsEl) labelsEl.removeEventListener('scroll', onColScroll);
    if (tlEl) tlEl.removeEventListener('scroll', onColScroll);
    if (actionsEl) actionsEl.removeEventListener('scroll', onColScroll);
    scrollHandlerAttached = false;
  }
});

function onRowActivate(row) {
  if (props.mode === 'student' || props.mode === 'student-present') {
    emit('student', row);
  }
}

function ariaLabel(ev) {
  const start = uiDisplayStart(ev) || plannedStart(ev);
  const end = uiDisplayEnd(ev) || plannedEnd(ev);
  const range = multiDayRangeText(ev);
  const rangeTxt = range ? ` (${range})` : '';
  return `${ev.subject} - ${ev.topic ? ev.topic : ''} ${toTime(start || (ev.startsAt || ev.starts_at))}–${toTime(end || (ev.endsAt || ev.ends_at))}${rangeTxt}`;
}
</script>

<style scoped>
.timeline-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 12px;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  font-weight: bolder;
  font-size: 12px;
  color: var(--muted);
  line-height: 16px;
}

.timeline-header .tick {
  white-space: nowrap;
}

/* Grid: links Labels, rechts gemeinsame Timeline mit mehreren Lanes */
.timeline-grid {
  display: grid;
  grid-template-columns: 150px 48px 1fr 48px;
  grid-template-rows: auto 1fr;
  gap: 2px;
  align-items: start;
  height: calc(100vh - 290px);
  overflow-y: scroll;
}

.labels-col {
  display: flex;
  flex-direction: column;
}

.actions-col {
  display: grid;
  align-items: center;
  margin-right: 4px;
}

/* rechte Aktions-Spalte leicht nach innen ziehen */
.actions-col-right {
  margin-right: 0;
  margin-left: 4px;
}

.row-action {
  display: flex;
  align-items: stretch;
  justify-content: center;
  /* gleiche Höhe wie Label-Zeilen, daher rowStyle(row) im Template */
  margin: 3px 0; /* vertikale Abstände analog .row-label */
}

.checkin-btn, .checkout-btn {
  width: 100%;
  height: 100%;
  border: 1px solid var(--success);
  color: var(--success);
  background: rgba(16, 185, 129, .1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.checkin-btn:hover {
  background: rgba(16, 185, 129, .08);
}

.checkin-btn:focus-visible {
  outline: 3px solid var(--focus);
  outline-offset: 2px;
}

/* Entlassen-Variante mit Warn-Farbe */
.checkout-btn {
  border-color: var(--warn);
  color: var(--warn);
  background: rgba(245, 158, 11, .10);
}

.checkout-btn:hover {
  background: rgba(245, 158, 11, .08);
}

.row-label {
  font-size: 12px;
  color: var(--muted);
  padding: 3px 3px 3px 5px;
  margin: 3px 4px 3px 0;
  border: 1px solid var(--border);
  border-radius: 8px 0 0 8px;
  min-height: 54px; /* vorher: height: 54px; */
  line-height: 15px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Schüler-Zeilen: Name links fett, rechts Status/Fehlzeit */
.row-label.is-student {
  color: var(--text);
  min-height: 54px; /* an TEACHER_ROW_HEIGHT/STUDENT_ROW_HEIGHT angepasst */
}

.row-label.clickable {
  cursor: pointer;
}

.row-label.is-student .row-name {
  font-weight: 500; /* bolder */
  font-size: 13px;
}

.row-label.is-student .row-right {
  display: block;
  width: 40px;
  text-align: right;
  line-height: 16px;
  opacity: .5;
}

.row-label.is-student.released {
  background: rgba(245, 158, 11, .14); /* gelber Ton wie bei Entlassung */
  border-color: var(--warn);
}

.row-label.is-student.has-notice {
  background: rgba(245, 158, 11, .14); /* gelber Ton bei Meldung */
  border-color: var(--warn);
}

.row-label.is-student.present {
  background: rgba(16, 185, 129, .1); /* pastell-grün */
  border-color: var(--success);
}

/* Entlassen-Icon rechts */
.release-icon {
  color: var(--warn);
  font-size: 14px;
}

.notice-icon {
  color: var(--warn);
  font-size: 14px;
}

/* Kumulierte Fehlzeit (nur Pflicht) – runde rote Badge */
.late-badge {
  font-size: 10px;
  padding: 1px 4px;
  border-radius: 10px;
  border: 1px solid var(--danger);
  background: var(--danger);
  color: #fff;
  font-weight: 600;
  line-height: 1;
}

.timeline-col .event-name {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
  font-weight: bold;
}

.timeline-col {
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--bg);
  border: 1px dashed var(--border);
}

.lane {
  position: relative;
  height: 60px;
  border-bottom: 1px dashed var(--border);
}

.lane:last-child {
  border-bottom: none;
}

/* Blocks innerhalb einer Lane absolut nach Zeit (links/width) */
.block {
  position: absolute;
  top: 3px;
  height: calc(100% - 6px);
  border: 1px solid var(--border);
  border-radius: 3px;
  background: var(--surface);
  padding: 3px;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 10;
}

.block.clickable {
  cursor: pointer;
}

.block.mandatory {
  background: rgba(37, 99, 235, .08);
}

.block.optional {
  background: rgba(16, 185, 129, .08);
}

/* Canceled: 5px hoch (über inline height), 45° schraffiert, ohne Inhalt */
.block.canceled {
  padding: 0;
  border-color: var(--danger);
  background-image: repeating-linear-gradient(
      45deg,
      rgba(239, 68, 68, .25) 0px,
      rgba(239, 68, 68, .25) 6px,
      transparent 6px,
      transparent 12px
  );
}

.block.canceled .block-label {
  display: none;
}

.block-label {
  font-size: 12px;
  line-height: 1.3;
  gap: 6px;
}

.meta {
  font-size: 10px;
  color: var(--muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sep {
  color: var(--muted);
}

.block-meta {
  display: flex;
  gap: 6px;
  align-items: center;
}

.chip {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--surface);
  border: 1px solid var(--border);
}

/* Zeit-Marker über alle Lanes */
.now-marker {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--primary-600);
  pointer-events: none;
  z-index: 1;
}

.badge {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
}

.badge-info {
  background: rgba(37, 99, 235, .1);
  border-color: #93C5FD;
}

.badge-warn {
  background: rgba(245, 158, 11, .1);
  border-color: #FCD34D;
}

.badge-success {
  background: rgba(16, 185, 129, .1);
  border-color: #6EE7B7;
}

.badge-danger {
  background: rgba(239, 68, 68, .1);
  border-color: #FCA5A5;
}

.muted {
  color: var(--muted);
}

/* Zeitbasierte Hintergründe (past/future), pastellig, nur wenn nicht canceled */
.block.future:not(.canceled) {
  background: rgba(107, 114, 128, .10); /* grau, muted */
}

.block.past:not(.canceled) {
  background: rgba(16, 185, 129, .2); /* grün, success */
}

/* Statusabhängige Rahmen */
.block.ready {
  border-style: dashed;
  background: rgba(107, 114, 128, .05) !important;
}

.block.started {
  border-style: solid;
}

/* Pflichtfach: markante Unterkante */
.block.mandatory:not(.canceled) {
  border-bottom-width: 2px;
  border-bottom-color: var(--text);
}

.block.completed:not(.canceled) {
  background: rgba(16, 185, 129, .5) !important;
}

/* Completed: kleines Check-Icon unten rechts */
.block.completed:not(.canceled)::after {
  content: '✓';
  position: absolute;
  right: 1px;
  bottom: 1px;
  font-size: 14px;
  font-weight: bold;
  color: var(--primary-600);
  line-height: 1;
  pointer-events: none;
}

/* Schüler-Ansicht: spezielle Hintergründe und Border-Regeln */
.block.st-bg-gray:not(.canceled) {
  background: rgba(107, 114, 128, .12);
}

/* rot */
.block.st-bg-red:not(.canceled) {
  background: rgba(240, 168, 168, 0.35);
}

/* gelb */
.block.st-bg-green:not(.canceled) {
  background: rgba(16, 185, 129, .14);
}

/* grün */

.block.st-bl-green {
  border-left: 2px solid var(--success);
}

.block.st-br-green {
  border-right: 2px solid var(--success);
}

.block.st-bl-red {
  border-left: 2px solid var(--danger);
}

.block.st-br-yellow {
  border-right: 2px solid var(--warn);
}

.block.st-br-dashed {
  border-right-style: dashed;
}

.block.st-bl-dashed {
  border-left-style: dashed;
}

.block.st-paused { /* nach 'entlassen': neutralisieren */
  border-left: 1px solid var(--border);
  border-right: 1px solid var(--border);
}

/* Präsenzmodus: Block darf außerhalb liegende Symbole anzeigen */
.block.st-ext-icons {
  overflow: visible;
}

/* Student-Present: besuchte Blöcke neutral-grün laut Vorgabe */
.block.st-visited:not(.canceled) {
  background: rgba(16, 185, 129, .2) !important;
}

.block.st-visited-current:not(.canceled) {
  background: rgba(16, 185, 129, .5) !important;
}

/* Lane-weite Marker für Ankunft/Entlassung (nicht an Blöcke gekoppelt) */
.stay-markers {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
}

.stay-caret {
  position: absolute;
  top: 30%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 2;
}

.stay-caret-in {
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 8px solid var(--success); /* Ankunft */
  margin-left: -8px;
}

.stay-caret-rel {
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 8px solid var(--warn); /* Entlassung */
  margin-left: 8px;
}

/* Hintergrund-Balken zwischen Ankunft und Entlassung */
.stay-range {
  position: absolute;
  height: 100%;
  background: rgba(230, 230, 230, .3);
  z-index: 1; /* unter den Carets (z-index: 2) */
  pointer-events: none;
  border: 0;
  top: 0;
}

.row-label {
  font-size: 12px;
  color: var(--muted);
  padding: 3px 3px 3px 5px;
  margin: 3px 4px 3px 0;
  border: 1px solid var(--border);
  border-radius: 8px 0 0 8px;
  min-height: 54px; /* vorher: height: 54px; */
  line-height: 15px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
}

.row-label-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
}

.row-class-watermark {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 34px;
  font-weight: 900;
  line-height: 1;
  color: var(--muted);
  opacity: 0.05;
  mix-blend-mode: multiply;
  pointer-events: none;
  z-index: 0;
}
</style>
